import { NSARouterMakerResult } from 'namirasoft-account-react';
import { SecretAlgorithm, SecretCreateRow, SecretRow } from 'namirasoft-secret';
import { NSBox, NSBoxEnum, NSBoxString, NSColumn } from 'namirasoft-site-react';
import { useRef, useState } from 'react';
import { SecretEntities } from '../SecretEntities';
import { SecretLayout } from '../SecretLayout';
import { SecretRouterProps } from '../SecretRouterProps';
import RetrieveValue from '../components/RetrieveValue';
import RetrieveValueDialog from '../components/RetrieveValueDialog';

interface SecretEditPageProps extends NSARouterMakerResult, SecretRouterProps
{
	isEdit: boolean;
}

interface SecretEditPageState
{
	show_retrieve_value: boolean;
}

function SecretEditPage(props: SecretEditPageProps)
{
	let [state, setState] = useState<SecretEditPageState>({ show_retrieve_value: false });

	let SecretLayout_Ref = useRef<SecretLayout>(null);
	let NSBoxString_Group = useRef<NSBoxString>(null);
	let NSBoxString_Name = useRef<NSBoxString>(null);
	let NSBoxEnum_Algorithm = useRef<NSBoxEnum<SecretAlgorithm>>(null);
	let RetrieveValueDialog_Value = useRef<RetrieveValueDialog>(null);
	let RetrieveValue_Value = useRef<RetrieveValue>(null);

	let id = SecretLayout_Ref.current?.NSALayout_Ref.current?.NSASectionEdit_Ref?.current?.getRow()?.id;

	return (
		<SecretLayout<SecretRow, SecretCreateRow>
			ref={SecretLayout_Ref}
			{...props}
			action={{
				title: `${props.isEdit ? "Edit" : "New"} Secret`,
				description: props.isEdit ? "This section allows you to update the stored information of an existing secret, including its name, group, or encryption settings." : "This section allows you to store a new secret by entering the group, name, algorithm, and value.",
				menus: {}
			}}
			pages={{
				entity: SecretEntities.secret(props, () =>
				{
					setState(prevState => ({ ...prevState, show_retrieve_value: true }))
				}),
				edit: {
					...props,
					ui: {
						tab_page_edit: {
							editor: {
								getInputRow: () =>
								{
									let group = NSBoxString_Group.current?.getValue() ?? "";
									let name = NSBoxString_Name.current?.getValue() ?? "";
									let algorithm = NSBoxEnum_Algorithm.current?.getValueEnumOne(SecretAlgorithm, SecretAlgorithm.ENCRYPT_AES256CCM) ?? SecretAlgorithm.ENCRYPT_AES256CCM;
									let value = RetrieveValue_Value.current?.getValue() ?? "";

									return {
										workspace_id: "",
										group,
										name,
										algorithm,
										value,
										secret_category: [],
										secret_field: [],
										secret_tag: []
									};
								},
								setRow: (row) =>
								{
									NSBoxString_Group.current?.setValue(row.group ?? "");
									NSBoxString_Name.current?.setValue(row.name ?? "");
									NSBoxEnum_Algorithm.current?.setValue(row.algorithm);
								}
							}
						}
					}
				}
			}}
		>
			<>
			<NSColumn>
				<NSBoxString
					ref={NSBoxString_Group}
					title='Group'
					required
					style={{ width: NSBox.width.double }}
					info={{ link: "https://namirasoft.com/secret/console-guide/secret/#field-group" }}
				/>
				<NSBoxString
					ref={NSBoxString_Name}
					title='Name'
					required
					style={{ width: NSBox.width.double }}
					info={{ link: "https://namirasoft.com/secret/console-guide/secret/#field-name" }}
				/>
				{
					!props.isEdit &&
					<NSBoxEnum<SecretAlgorithm>
						ref={NSBoxEnum_Algorithm}
						title='Algorithm'
						required
						getEnumObject={() => SecretAlgorithm}
						multiple={false}
						searchable={false}
						style={{ width: NSBox.width.double }}
						info={{ link: "https://namirasoft.com/secret/console-guide/secret/#field-algorithm" }}
					/>
				}
				{
					!props.isEdit &&
					<RetrieveValue ref={RetrieveValue_Value} />
				}
			</NSColumn>

			{
				(props.isEdit && state.show_retrieve_value)
					?
					<RetrieveValueDialog
						ref={RetrieveValueDialog_Value}
						{...props}
						id={id ?? ""}
						onClose={() =>
						{
							setState(prevState => ({ ...prevState, show_retrieve_value: false }));
						}}
					/>
					:
					<></>
			}
			</>
		</SecretLayout>
	);
}

export default SecretEditPage;